import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Box, LoadError, Loading, MissingRates, Tile, eur, monthLabel, num, pct } from './costsFormat';

/**
 * Resumen del mes: estimado contra factura por proveedor, fijo y variable, y
 * coste por deudor atendido por línea. La factura manda; el estimado es para
 * verlo a diario. Por encima del 15 % de desviación, en rojo.
 */
const CostsSummary = ({ month, onGoToRates }) => {
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    let live = true;
    setState({ status: 'loading', data: null });
    api
      .get('/costs/summary', { params: { month } })
      .then(({ data }) => live && setState({ status: 'ok', data: data.data }))
      .catch(() => live && setState({ status: 'error', data: null }));
    return () => {
      live = false;
    };
  }, [month]);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'error') return <LoadError what='el resumen' />;
  const s = state.data;

  if (!s.rates_loaded) {
    return (
      <div className='bg-white border border-slate-200 rounded-lg px-4 py-6 text-sm text-slate-600'>
        Todavía no hay tarifas, así que no se puede calcular nada.{' '}
        <button type='button' className='text-indigo-600 hover:underline' onClick={onGoToRates}>
          Cárgalas en la pestaña Tarifas
        </button>
        .
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
        <Tile label={`Estimado · ${monthLabel(s.month)}`} value={eur(s.total.estimated_eur)} sub={`Fijo ${eur(s.total.fixed_eur)} · variable ${eur(s.total.variable_eur)}`} />
        <Tile label='Facturas del mes' value={eur(s.total.invoice_eur)} sub={s.total.invoice_eur == null ? 'Sin facturas metidas' : null} />
        <Tile label='Deudores atendidos' value={num(s.total.attended)} sub='Con alguna comunicación saliente' />
        <Tile label='Coste por deudor atendido' value={eur(s.total.cost_per_attended)} />
      </div>

      <MissingRates missing={s.missing} />

      <Box title='Por proveedor'>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='text-xs text-slate-500 text-left'>
              <tr>
                <th className='px-3 py-2 font-medium'>Proveedor</th>
                <th className='px-3 py-2 font-medium text-right'>Fijo</th>
                <th className='px-3 py-2 font-medium text-right'>Variable</th>
                <th className='px-3 py-2 font-medium text-right'>Estimado</th>
                <th className='px-3 py-2 font-medium text-right'>Factura</th>
                <th className='px-3 py-2 font-medium text-right'>Desviación</th>
              </tr>
            </thead>
            <tbody>
              {s.providers.map(p => (
                <tr key={p.provider} className='border-t border-slate-100'>
                  <td className='px-3 py-2'>{p.label}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap'>{eur(p.fixed_eur)}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap'>{eur(p.variable_eur)}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap font-medium'>{eur(p.estimated_eur)}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap'>{p.invoice_eur == null ? <span className='text-slate-400'>Sin factura</span> : eur(p.invoice_eur)}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${p.alert ? 'text-rose-700 font-medium' : 'text-slate-600'}`} data-testid={`deviation-${p.provider}`}>
                    {pct(p.deviation_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      <Box title='Por línea'>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='text-xs text-slate-500 text-left'>
              <tr>
                <th className='px-3 py-2 font-medium'>Línea</th>
                <th className='px-3 py-2 font-medium text-right'>Fijo</th>
                <th className='px-3 py-2 font-medium text-right'>Variable</th>
                <th className='px-3 py-2 font-medium text-right'>Total</th>
                <th className='px-3 py-2 font-medium text-right'>Deudores atendidos</th>
                <th className='px-3 py-2 font-medium text-right'>Por deudor</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.map(l => (
                <tr key={l.business_line || 'none'} className='border-t border-slate-100'>
                  <td className='px-3 py-2'>{l.label}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap'>{eur(l.fixed_eur)}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap'>{eur(l.variable_eur)}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap font-medium'>{eur(l.estimated_eur)}</td>
                  <td className='px-3 py-2 text-right'>{num(l.attended)}</td>
                  <td className='px-3 py-2 text-right whitespace-nowrap'>{eur(l.cost_per_attended)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className='text-xs text-slate-500 mt-2'>
          Los fijos van a la línea a la que pertenecen (el gestor y Maytapi a recobro, el plan de Kapso a AR). No se reparten por empresa.
        </p>
      </Box>

      <Box title='Últimos 6 meses'>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='text-xs text-slate-500 text-left'>
              <tr>
                <th className='px-3 py-2 font-medium'>Mes</th>
                <th className='px-3 py-2 font-medium text-right'>Estimado</th>
                <th className='px-3 py-2 font-medium text-right'>Facturas</th>
              </tr>
            </thead>
            <tbody>
              {s.history.map(h => (
                <tr key={h.month} className='border-t border-slate-100'>
                  <td className='px-3 py-2 capitalize'>{monthLabel(h.month)}</td>
                  <td className='px-3 py-2 text-right'>{eur(h.estimated_eur)}</td>
                  <td className='px-3 py-2 text-right'>{h.invoice_eur == null ? <span className='text-slate-400'>—</span> : eur(h.invoice_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className='text-xs text-slate-500 mt-2'>Cada mes se calcula con las tarifas que estaban vigentes entonces.</p>
      </Box>
    </div>
  );
};

export default CostsSummary;
